/**
 * Speech-recognition transcript normalisation.
 *
 * The browser recogniser mishears brand terms: "EV Initiative" comes back as
 * "eevee initiative" and similar, so a driver saying the company name gets it
 * typed wrong every time. This maps known mishearings back to the correct
 * written form before the text reaches any input.
 *
 * THE MAP IS DELIBERATELY EMPTY. A guessed mapping that half works is harder
 * to debug than no mapping at all, because a wrong entry looks like a
 * recogniser problem rather than a config problem — you would be chasing the
 * browser when the bug is in this file. Entries get added ONLY from real
 * captured output (tools/recogniser-capture.html), never from a guess about
 * what a recogniser probably returns.
 *
 * With the map empty this function is an identity pass, so wiring it now is
 * behaviour-neutral: both hooks call it, and populating the table later is a
 * one-file change that needs no client work.
 *
 * SHARED ON PURPOSE. The driver PWA and the admin app have separate,
 * duplicated recognition hooks. Fixing one would leave the other wrong, and
 * two copies of a mapping table is how they drift apart.
 */
/**
 * Known mishearings, longest-first at match time so a longer phrase wins over
 * a shorter one contained in it ("eevee initiative" must beat "eevee").
 *
 * POPULATE FROM CAPTURES ONLY. Each entry should be something a recogniser
 * was observed to return, ideally in more than one accent.
 */
export const TRANSCRIPT_RULES = [
// (empty — awaiting real capture output)
];
/** Escape a string for literal use inside a RegExp. */
function escapeRe(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
/**
 * Rewrite known mishearings in a recognition transcript.
 *
 * Case-insensitive and whole-phrase (word-boundary anchored), so "eevee" in
 * the middle of a sentence is caught but "eeveelution" is not. Rules are
 * applied longest-heard-first. Returns the input unchanged when no rule
 * matches — and unconditionally while the table is empty.
 */
export function normalizeTranscript(text) {
    if (!text || TRANSCRIPT_RULES.length === 0)
        return text;
    let out = text;
    const ordered = [...TRANSCRIPT_RULES].sort((a, b) => b.heard.length - a.heard.length);
    for (const rule of ordered) {
        out = out.replace(new RegExp(`\\b${escapeRe(rule.heard)}\\b`, 'gi'), rule.write);
    }
    return out;
}
//# sourceMappingURL=normalizeTranscript.js.map